const BASE = "https://sprankelien.be";
const DEFAULT_OG_IMAGE = `${BASE}/og-image.jpg`;

export const organizationJsonLd = {
  "@context": "https://schema.org",
  // Dual type so Google can classify us both as a physical local business
  // AND as a health/wellbeing practice — improves eligibility for the
  // Local Business rich result and the Health search intent surface.
  "@type": ["LocalBusiness", "HealthAndBeautyBusiness"],
  "@id": `${BASE}/#business`,
  name: "Praktijk Sprankelien",
  description:
    "Warme, professionele begeleiding voor kinderen in Ramsel. Speltherapie, kinderyoga en zomerkampen in een veilige, huiselijke sfeer.",
  url: BASE,
  image: DEFAULT_OG_IMAGE,
  logo: DEFAULT_OG_IMAGE,
  telephone: "+32476796175",
  email: "hildenys@live.be",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Ramsel",
    addressRegion: "Antwerpen",
    postalCode: "2230",
    addressCountry: "BE",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 51.0667,
    longitude: 4.8833,
  },
  areaServed: [
    { "@type": "City", name: "Ramsel" },
    { "@type": "City", name: "Herselt" },
    { "@type": "City", name: "Aarschot" },
    { "@type": "City", name: "Westerlo" },
    { "@type": "City", name: "Scherpenheuvel-Zichem" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    },
  ],
  priceRange: "€€",
  sameAs: [] as string[],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Diensten",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Speltherapie & begeleiding",
          description: "Persoonlijke begeleiding voor kinderen bij emoties, stress en zelfvertrouwen in Ramsel.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Kinderyoga",
          description: "Yoga en ontspanning voor kinderen in Ramsel — ademhaling, beweging en verbeelding.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Zomerkampen",
          description: "Avontuurlijke zomerkampen in de natuur voor kinderen van 6-12 jaar in Ramsel.",
        },
      },
    ],
  },
};

export const breadcrumb = (items: { name: string; path: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${BASE}${item.path}`,
  })),
});

export const serviceJsonLd = (
  name: string,
  description: string,
  url: string,
  price?: string,
) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  url: `${BASE}${url}`,
  areaServed: [
    { "@type": "City", name: "Ramsel" },
    { "@type": "City", name: "Herselt" },
    { "@type": "City", name: "Aarschot" },
    { "@type": "City", name: "Westerlo" },
    { "@type": "City", name: "Scherpenheuvel-Zichem" },
  ],
  provider: {
    // Reference the LocalBusiness by @id so Google links this Service to the
    // physical practice already declared on the homepage instead of treating
    // it as a duplicate business entity.
    "@id": `${BASE}/#business`,
  },
  ...(price ? { offers: { "@type": "Offer", price, priceCurrency: "EUR" } } : {}),
});

export const articleJsonLd = (
  title: string,
  excerpt: string,
  slug: string,
  date: string,
  options?: { image?: string; dateModified?: string },
) => {
  const url = `${BASE}/blog/${slug}`;
  const image = options?.image
    ? options.image.startsWith("http")
      ? options.image
      : `${BASE}${options.image.startsWith("/") ? options.image : `/${options.image}`}`
    : DEFAULT_OG_IMAGE;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    description: excerpt,
    url,
    image,
    datePublished: date,
    dateModified: options?.dateModified ?? date,
    author: { "@type": "Person", name: "Hilde Nys" },
    publisher: {
      "@type": "Organization",
      name: "Praktijk Sprankelien",
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_OG_IMAGE,
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };
};
